
import {
  Component,
  DestroyRef,
  ElementRef,
  inject,
  Input,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NgControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import {
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { CerrixButtonComponent } from '../cerrix-button/cerrix-button.component';
import { CerrixIconComponent } from '../cerrix-icon/cerrix-icon.component';
import { CerrixInputCurrencyComponent } from '../cerrix-input-currency/cerrix-input-currency.component';
import { CerrixSelectComponent } from '../cerrix-select/cerrix-select.component';
import { CerrixSelectableListComponent } from '../cerrix-selectable-list/cerrix-selectable-list.component';
import { CerrixTreeComponent } from '../cerrix-tree/cerrix-tree.component';
import { BaseModel } from '../models/base.model';
import { FinancialImpactRow } from '../models/financial-impact/financial-impact-row.model';
import { FinancialImpact } from '../models/financial-impact/financial-impact.model';
import { CurrencyInterface } from '../models/interfaces/currency.model';
import { SelectOptionModel } from '../models/select-option.model';
import { TreeItemNode } from '../models/tree/tree-item-node.model';
import { CerrixDialogService } from '../services/cerrix-dialog.service';
import { DefaultErrorStateMatcher } from '../validation/DefaultErrorStateMatcher';

@Component({
    selector: 'cerrix-financial-impact',
    imports: [
    MatIconModule,
    MatTabsModule,
    MatDialogModule,
    CerrixIconComponent,
    CerrixInputCurrencyComponent,
    CerrixSelectComponent,
    CerrixTreeComponent,
    FormsModule,
    ReactiveFormsModule,
    CerrixSelectableListComponent,
    CerrixButtonComponent
],
    templateUrl: './cerrix-financial-impact.component.html',
    styleUrl: './cerrix-financial-impact.component.scss'
})
export class CerrixFinancialImpactComponent
  implements OnInit, ControlValueAccessor
{
  protected ngControl = inject(NgControl, { optional: true, self: true });
  private readonly destroyRef = inject(DestroyRef);

  @Input() public value: string | undefined = undefined;
  @Input() public forceSingleCurrency: boolean = true;
  @Input() public internalOrganizations: TreeItemNode[] | undefined = undefined;
  @Input() public thirdParties: BaseModel[] | undefined = undefined;
  @Input() public disabled: boolean = this.ngControl?.disabled ?? false;
  private _currencyList: CurrencyInterface[] = [];
  @Input()
  set currencyList(value: CurrencyInterface[]) {
    if (value === undefined && this._currencyList === value) {
      return;
    }

    value.forEach(c => {
      this.currencyOptions.push({
        id: c.id,
        name: c.name,
      });
    });
    if (this._selectedCurrency === '') {
      this._selectedCurrency = this.currencyOptions[0].id;
    }
    this.financialImpact.rows.forEach(row => {
      if (row.currencyPrefix === undefined) {
        row.currencyPrefix = value.find(c => c.id === row.currencyId)?.sign;
      }
    });
    if (this.financialImpact.rows.length > 0) {
      this.updateFinancialImpact();
      this.ngControl?.control?.markAsPristine();
      this.ngControl?.control?.markAsUntouched();
    }

    this._currencyList = value;
  }
  get currencyList() {
    return this._currencyList;
  }

  protected financialImpact: FinancialImpact = {
    rows: [],
    totals: [],
  };
  protected currencyOptions: SelectOptionModel[] = [];

  private readonly dialogService = inject(CerrixDialogService);
  private readonly dialog = inject(MatDialog);
  private dialogRef!: MatDialogRef<any, any>;
  @ViewChild('addRowDialog') protected modalTemplate!: TemplateRef<any>;
  @ViewChild(CerrixTreeComponent) protected treeSelectRef!: CerrixTreeComponent;
  @ViewChild(CerrixSelectableListComponent)
  protected selectableListRef!: CerrixSelectableListComponent;
  @ViewChild('body') protected bodyRef!: ElementRef<HTMLElement>;

  protected dialogItemsSelected: boolean = false;
  protected _selectedCurrency: string = '';

  protected errorMatcher!: ErrorStateMatcher;

  constructor() {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
      this.errorMatcher = new DefaultErrorStateMatcher();
    }
  }

  ngOnInit(): void {
    if (this.value !== undefined && this.value !== null) {
      const parsedValue = JSON.parse(this.value);
      if (
        parsedValue?.rows === undefined ||
        parsedValue?.totals === undefined
      ) {
        this.financialImpact.rows = parsedValue;
        this.financialImpact.totals = this.getTotalsPerCurrency();
      } else {
        this.financialImpact.rows = parsedValue.rows;
        this.financialImpact.totals = parsedValue.totals;
      }
      this.financialImpact.rows.forEach(row => {
        if (row.total === undefined) {
          row.total = this.calculateTotal(
            this.getParsedValue(row.gross),
            this.getParsedValue(row.insured),
            this.getParsedValue(row.otherRecovery)
          ).replace('.', ',');
        }
      });
      if (this.financialImpact.rows.length > 0) {
        this._selectedCurrency = this.financialImpact.rows[0].currencyId;
      }
      this.updateFinancialImpact();
      this.ngControl?.control?.markAsPristine();
      this.ngControl?.control?.markAsUntouched();
    }
  }

  private onValueChange = (value: string | undefined) => {
    this.onTouched();
  };

  private onTouched = () => {
    this.ngControl?.control?.markAsDirty();
    this.ngControl?.control?.markAsTouched();
  };

  public writeValue(value: string): void {
    this.value = value;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public registerOnChange(onChange: (value: string | undefined) => void): void {
    this.onValueChange = onChange;
  }
  public registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  protected onScroll(event: Event): void {
    if (this.bodyRef.nativeElement) {
      this.bodyRef.nativeElement.scrollLeft = (
        event.target as HTMLElement
      ).scrollLeft;
    }
  }

  protected openDialog(): void {
    if (
      (this.internalOrganizations === undefined &&
        this.thirdParties === undefined) ||
      (this.internalOrganizations?.length === 0 &&
        this.thirdParties?.length === 0)
    ) {
      this.dialogService.openWarningConfirmDialog({
        data: {
          title: 'No data available',
          message: `To use this component you must populate the "Caused By" or "Affected" on the Details tab.`,
        },
      });
    } else {
      this.dialogRef = this.dialog.open(this.modalTemplate, {
        width: '1080px',
        height: '680px',
        panelClass: 'cerrix-financial-impact-dialog',
      });
    }
  }

  protected onApply() {
    const currencyId = this.forceSingleCurrency
      ? this._selectedCurrency
      : (this.currencyList[0].id ?? '');
    const currencyPrefix =
      this.currencyList?.find(c => c.id === currencyId)?.sign ?? '';

    const selectedNodes = this.treeSelectRef?.getSelectedNodes() ?? [];
    for (const internalOrganization of this.internalOrganizations?.filter(n =>
      selectedNodes.some(sn => sn.id === n.id)
    ) ?? []) {
      this.addRow(
        internalOrganization.id,
        internalOrganization.name,
        'internal-organization',
        currencyId,
        currencyPrefix
      );
    }

    for (const selectedItem of this.selectableListRef?.getSelectedItems() ??
      []) {
      this.addRow(
        selectedItem.id,
        selectedItem.name,
        'third-party',
        currencyId,
        currencyPrefix
      );
    }

    this.updateFinancialImpact();

    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  private addRow(
    id: string,
    name: string,
    type: string,
    currencyId: string,
    currencyPrefix: string
  ): void {
    this.financialImpact.rows?.push({
      id,
      name,
      type: type as 'internal-organization' | 'third-party',
      currencyId,
      currencyPrefix,
      gross: '',
      insured: '',
      otherRecovery: '',
      total: this.calculateTotal(0, 0, 0).replace('.', ','),
    });
  }

  protected removeRow(index: number): void {
    const row = this.financialImpact.rows?.[index];
    if (row === undefined) {
      return;
    }

    const title = `Delete ${row.type === 'internal-organization' ? 'Internal Organization' : 'Third Party'}`;
    this.dialogService.openWarningConfirmDialog({
      data: {
        promptUiid: 'delete-financial-impact-row-dialog-uiid',
        title: title,
        message: `Are you sure you want to delete ${row.name} from the Financial Impact table?`,
        positiveButton: {
          text: title,
          buttonClass: 'warning',
          onAction: () => {
            this.financialImpact.rows?.splice(index, 1);
            this.updateFinancialImpact();
            return true;
          },
        },
        negativeButton: {
          text: 'Cancel',
          buttonClass: 'cancel',
          onAction: () => {
            return true;
          },
        },
      },
    });
  }

  protected updateAllCurrency(currencyId: string): void {
    this._selectedCurrency = currencyId;
    const currencyPrefix = this.currencyList.find(
      c => c.id === currencyId
    )?.sign;
    this.financialImpact.rows?.forEach(row => {
      row.currencyId = this._selectedCurrency;
      row.currencyPrefix = currencyPrefix;
    });

    this.updateFinancialImpact();
  }

  protected updateCurrency(currencyId: string, row: FinancialImpactRow): void {
    row.currencyId = currencyId;
    row.currencyPrefix = this.currencyList.find(c => c.id === currencyId)?.sign;

    this.updateFinancialImpact();
  }

  protected updateFinancialImpact(row?: FinancialImpactRow): void {
    if(this.financialImpact.rows.length > 0){
      this.sort();
    }
    if (row) {
      row.total = this.getRowTotal(row);
    }
    this.financialImpact.totals = this.getTotalsPerCurrency();
    this.value =
      this.financialImpact.rows.length > 0
        ? JSON.stringify(this.financialImpact)
        : undefined;
    this.onValueChange(this.value);
  }

  private getRowTotal(row: FinancialImpactRow): string {
    let gross = this.getParsedValue(row.gross);
    let insured = this.getParsedValue(row.insured);
    let otherRecovery = this.getParsedValue(row.otherRecovery);

    return this.calculateTotal(gross, insured, otherRecovery).replace('.', ',');
  }

  private calculateTotal(
    gross: number,
    insured: number,
    otherRecovery: number
  ): string {
    return (gross - insured - otherRecovery).toFixed(2);
  }

  private getTotalsPerCurrency(): FinancialImpactRow[] {
    const totalsCalculated: FinancialImpactRow[] = [];
    const totals: any[] = [];

    this.financialImpact.rows?.forEach(row => {
      let currency = this.currencyList?.find(c => c.id === row.currencyId);
      if (currency === undefined) {
        currency = {
          id: '',
          name: '',
          sign: '',
        };
      }

      let totalsRow = totals.find(
        totalsRow => totalsRow.name === currency.name
      );

      let gross = this.getParsedValue(row.gross);
      let insured = this.getParsedValue(row.insured);
      let otherRecovery = this.getParsedValue(row.otherRecovery);

      if (totalsRow === undefined) {
        totals.push({
          id: currency.id,
          name: currency.name,
          currencyPrefix: currency.sign,
          gross: gross,
          insured: insured,
          otherRecovery: otherRecovery,
        });
      } else {
        totalsRow.gross += gross;
        totalsRow.insured += insured;
        totalsRow.otherRecovery += otherRecovery;
      }
    });

    for (const row of totals) {
      totalsCalculated.push({
        id: row.id,
        name: row.name,
        type: '',
        currencyId: row.id,
        currencyPrefix: row.currencyPrefix,
        gross: row.gross.toFixed(2).replace('.', ','),
        insured: row.insured.toFixed(2).replace('.', ','),
        otherRecovery: row.otherRecovery.toFixed(2).replace('.', ','),
        total: this.calculateTotal(
          row.gross,
          row.insured,
          row.otherRecovery
        ).replace('.', ','),
      });
    }

    totalsCalculated.sort((a, b) => {
      const aIndex = this.currencyList?.findIndex(c => c.id === a.currencyId);
      const bIndex = this.currencyList?.findIndex(c => c.id === b.currencyId);
      return aIndex - bIndex;
    });

    return totalsCalculated;
  }

  private getParsedValue(value: string): number {
    let parsedValue = value
      ? parseFloat(value.replace(/\./g, '').replace(/,/g, '.'))
      : 0;
    if (isNaN(parsedValue)) {
      return 0;
    }

    return parsedValue;
  }

  private sort(){
    let orgs: FinancialImpactRow[] = [];
    let tps: FinancialImpactRow[] = [];
    this.financialImpact.rows.forEach(row=>{
      switch(row.type){
        case 'internal-organization':
          orgs.push(row);
          break;
        case 'third-party':
          tps.push(row);
          break;
      }
    });

    tps.sort((a, b) => a.name.localeCompare(b.name));
    this.financialImpact.rows = [...orgs, ...tps];
    
  }
}
